import axios from 'axios';

// Base URL configuration
const API_URL = 'https://api.devstc.ispcorp.pe';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor to add Authorization header
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor to handle 401/403 errors (Inactivity or Invalid Token)
api.interceptors.response.use(
    (response) => {
        // Ping session on successful response (optional, or handled separately)
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            // Prevent redirect loop if the error comes from the login endpoint itself
            if (error.config.url && error.config.url.includes('login.php')) {
                return Promise.reject(error);
            }

            // Token expired or invalid
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const authService = {
    login: async (username, password) => {
        const response = await api.post('/login.php', { username, password, portal: 'admin' });
        return response.data;
    },
    getNavigation: async () => {
        const response = await api.get('/navigation.php');
        return response.data;
    },
    pingSession: async () => {
        try {
            await api.post('/session_ping.php');
        } catch (error) {
            console.error("Session ping failed", error);
        }
    }
};

export const participantService = {
    list: async (params) => {
        const response = await api.get('/participants/list.php', { params });
        return response.data;
    },
    getFilters: async () => {
        const response = await api.get('/participants/get_filters.php');
        return response.data;
    },
    getDetail: async (uuid) => {
        const response = await api.get(`/participants/detail.php`, { params: { uuid } });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/participants/create.php', data);
        return response.data;
    },
    update: async (data) => {
        const response = await api.post('/participants/update.php', data);
        return response.data;
    },
    delete: async (uuid) => {
        const response = await api.post('/participants/delete.php', { uuid });
        return response.data;
    },
    searchUbigeo: async (query) => {
        const response = await api.get('/participants/ubigeo_search.php', { params: { q: query } });
        return response.data;
    },
    // Cards
    getCards: async (participantUuid) => {
        const response = await api.get('/participants/cards.php', { params: { uuid: participantUuid } });
        return response.data;
    },
    createCard: async (data) => {
        const response = await api.post('/participants/cards.php', data);
        return response.data;
    },
    updateCard: async (data) => {
        const response = await api.put('/participants/cards.php', data);
        return response.data;
    },
    deleteCard: async (cardId) => {
        const response = await api.delete('/participants/cards.php', { params: { id: cardId } });
        return response.data;
    },
    getCardTags: async () => {
        const response = await api.get('/participants/cards.php', { params: { action: 'tags' } });
        return response.data;
    }
};

export const groupService = {
    list: async (search) => {
        const response = await api.get('/groups/list.php', { params: { search } });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/groups/create.php', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/groups/update.php`, data, { params: { id } });
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/groups/delete.php`, { params: { id } });
        return response.data;
    }
};

export const tagService = {
    list: async () => {
        const response = await api.get('/tags/list.php');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/tags/create.php', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/tags/update.php`, { ...data, id });
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/tags/delete.php`, { data: { id } });
        return response.data;
    },
    getTagCards: async (tagId, search = null) => {
        const response = await api.get('/tags/cards.php', { params: { id: tagId, search } });
        return response.data;
    }
};

export const userService = {
    list: async (params) => {
        const response = await api.get('/users/list.php', { params });
        return response.data;
    },
    getRoles: async () => {
        const response = await api.get('/users/roles.php');
        return response.data;
    },
    getDetail: async (uuid) => {
        const response = await api.get('/users/detail.php', { params: { uuid } });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/users/create.php', data);
        return response.data;
    },
    update: async (data) => {
        const response = await api.post('/users/update.php', data);
        return response.data;
    },
    delete: async (uuid) => {
        const response = await api.post('/users/delete.php', { uuid });
        return response.data;
    },
    getProfile: async () => {
        const response = await api.get('/users/get_profile.php');
        return response.data;
    },
    updateProfile: async (data) => {
        const response = await api.post('/users/update_profile.php', data);
        return response.data;
    },
    getSipCredentials: async () => {
        const response = await api.get('/pbx/get_profile_sip.php');
        return response.data;
    },
    getUsersExportUrl: (params) => {
        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== 'undefined')
        );
        const queryString = new URLSearchParams(cleanParams).toString();
        const token = localStorage.getItem('token');
        return `${API_URL}/users/list.php?action=export&${queryString}&token=${token}`;
    }
};

export const roleService = {
    list: async (portal = null) => {
        const response = await api.get('/roles/list.php', { params: { portal } });
        return response.data;
    },
    getDetail: async (uuid) => {
        const response = await api.get('/roles/detail.php', { params: { uuid } });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/roles/create.php', data);
        return response.data;
    },
    update: async (data) => {
        const response = await api.post('/roles/update.php', data);
        return response.data;
    },
    delete: async (uuid, target_role_uuid = null) => {
        const response = await api.post('/roles/delete.php', { uuid, target_role_uuid });
        return response.data;
    },
    syncPermissions: async (uuid, permissions) => {
        const response = await api.post('/roles/permissions_sync.php', { uuid, permissions });
        return response.data;
    },
    previewPortalChange: async (uuid) => {
        const response = await api.get('/roles/preview_portal_change.php', { params: { uuid } });
        return response.data;
    },
    changePortal: async (uuid, new_portal) => {
        const response = await api.post('/roles/change_portal.php', { uuid, new_portal });
        return response.data;
    },
    duplicate: async (uuid) => {
        const response = await api.post('/roles/duplicate.php', { uuid });
        return response.data;
    },
    getAllPermissions: async () => {
        const response = await api.get('/roles/all_permissions.php');
        return response.data;
    }
};

export const auditService = {
    list: async (params) => {
        const response = await api.get('/system/audit/list.php', { params });
        return response.data;
    },
    getFilters: async () => {
        const response = await api.get('/system/audit/get_filters.php');
        return response.data;
    },
    getDetail: async (id) => {
        const response = await api.get('/system/audit/detail.php', { params: { id } });
        return response.data;
    }
};

export const pbxService = {
    listForms: async (search = null) => {
        const response = await api.get('/pbx/forms/list.php', { params: { search } });
        return response.data;
    },
    getForm: async (id) => {
        const response = await api.get('/pbx/forms/list.php', { params: { id } });
        return response.data;
    },
    createForm: async (data) => {
        const response = await api.post('/pbx/forms/create.php', data);
        return response.data;
    },
    updateForm: async (data) => {
        const response = await api.put('/pbx/forms/update.php', data);
        return response.data;
    },
    deleteForm: async (id) => {
        const response = await api.delete('/pbx/forms/delete.php', { params: { id } });
        return response.data;
    },
    syncFields: async (formId, fields) => {
        const response = await api.put('/pbx/forms/sync_fields.php', { form_id: formId, fields });
        return response.data;
    }
};

export const breakService = {
    list: async (params = {}) => {
        const response = await api.get('/pbx/breaks/index.php', { params });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/pbx/breaks/create.php', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put('/pbx/breaks/update.php', { ...data, id });
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete('/pbx/breaks/delete.php', { params: { id } });
        return response.data;
    }
};

export const blacklistService = {
    list: async (params = {}) => {
        const response = await api.get('/pbx/blacklist/index.php', { params });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/pbx/blacklist/create.php', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put('/pbx/blacklist/update.php', { ...data, id });
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete('/pbx/blacklist/delete.php', { params: { id } });
        return response.data;
    }
};

export const campaignService = {
    list: async () => {
        const response = await api.get('/pbx/campaigns/list.php');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/pbx/campaigns/create.php', data);
        return response.data;
    },
    update: async (data) => {
        const response = await api.post('/pbx/campaigns/update.php', data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.post('/pbx/campaigns/delete.php', { id });
        return response.data;
    },
    assignParticipants: async (data) => {
        const response = await api.post('/pbx/campaigns/assign_participants.php', data);
        return response.data;
    }
};

export const campaignAgentService = {
    list: async (campaignId) => {
        const response = await api.get(`/pbx/campaigns/agents/list.php?campaign_id=${campaignId}`);
        return response.data;
    },
    getAvailable: async (campaignId) => {
        const response = await api.get(`/pbx/campaigns/agents/available.php?campaign_id=${campaignId}`);
        return response.data;
    },
    assign: async (data) => {
        const response = await api.post('/pbx/campaigns/agents/assign.php', data);
        return response.data;
    },
    remove: async (data) => {
        const response = await api.post('/pbx/campaigns/agents/remove.php', data);
        return response.data;
    }
};

export const campaignParticipantService = {
    list: async (campaignId) => {
        const response = await api.get(`/pbx/campaigns/participants/list.php?campaign_id=${campaignId}`);
        return response.data;
    },
    export: (campaignId, batchTag) => {
        // Direct download URL
        const token = localStorage.getItem('token');
        const url = `${API_URL}/pbx/campaigns/participants/export.php?campaign_id=${campaignId}&batch_tag=${encodeURIComponent(batchTag)}&token=${token}`;
        window.open(url, '_blank');
    }
};

export const reportService = {
    getCDR: async (params) => {
        const response = await api.get('/pbx/reports/cdr.php', { params });
        return response.data;
    },
    getCDRExportUrl: (params) => {
        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== 'undefined')
        );
        const queryString = new URLSearchParams(cleanParams).toString();
        const token = localStorage.getItem('token');
        return `${API_URL}/pbx/reports/cdr.php?action=export&${queryString}&token=${token}`;
    },
    getCallDetail: async (id) => {
        const response = await api.get('/pbx/reports/cdr.php', { params: { action: 'detail', id } });
        return response.data;
    },
    getRecordingUrl: (id, download = false) => {
        const token = localStorage.getItem('token');
        let url = `${API_URL}/pbx/reports/cdr.php?action=recording&id=${id}&token=${token}`;
        if (download) url += '&download=1';
        return url;
    },
    getBreaksReport: async (params) => {
        const response = await api.get('/pbx/reports/breaks.php', { params });
        return response.data;
    },
    getBreaksReportExportUrl: (params) => {
        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== 'undefined')
        );
        const queryString = new URLSearchParams(cleanParams).toString();
        const token = localStorage.getItem('token');
        return `${API_URL}/pbx/reports/breaks.php?action=export&${queryString}&token=${token}`;
    },
    getManualCallsStats: async (params) => {
        const response = await api.get('/pbx/reports/manual_calls.php', { params });
        return response.data;
    },
    getCampaignCallsReport: async (params) => {
        const response = await api.get('/pbx/reports/campaign_calls.php', { params });
        return response.data;
    },
    getServiceStats: async (params) => {
        const response = await api.get('/pbx/reports/service_stats.php', { params });
        return response.data;
    },
    getGeoCoverageReport: async (params) => {
        const response = await api.get('/pbx/reports/geo_coverage.php', { params });
        return response.data;
    },
    getServiceTimeReport: async (params) => {
        const response = await api.get('/pbx/reports/service_time.php', { params });
        return response.data;
    }
};

export const agentService = {
    getMonitoring: async () => {
        const response = await api.get('/pbx/agents/monitoring.php');
        return response.data;
    }
};

export const dashboardService = {
    getMetrics: async () => {
        const response = await api.get('/pbx/dashboard/metrics.php');
        return response.data;
    }
};

export const pbxReportCatalogService = {
    getCampaigns: async () => {
        const response = await api.get('/pbx/campaigns/list.php');
        return response.data;
    },
    getUsersWithExtension: async (portal = null) => {
        let url = '/users/list.php?has_extension=1';
        if (portal) url += `&portal=${portal}`;
        const response = await api.get(url);
        return response.data;
    }
};

export default api;
